import React from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import MdRenderer from "crqlar-react-common/dist/components/MdRenderer";
import MdEditor from "crqlar-react-common/dist/components/MdEditor";
import {ShortTextQuestion} from "crqlar-react-common/dist/models/forms/form";
import {useTranslation} from "react-i18next";

interface ShortTextInputProps {
    question: ShortTextQuestion;
    onQuestionUpdate: (question: ShortTextQuestion) => void;
    isSelected?: boolean;
    onSelect?: () => void;
}

const ShortTextInput: React.FC<ShortTextInputProps> = ({
                                                           question,
                                                           onQuestionUpdate,
                                                           isSelected = false,
                                                           onSelect
                                                       }) => {
    const {t} = useTranslation();

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            title: e.target.value
        });
    };

    const handleDescriptionChange = (value?: string) => {
        onQuestionUpdate({
            ...question,
            description: value || ''
        });
    };

    const handlePlaceholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            placeholder: e.target.value
        });
    };

    const handleRequiredChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            required: e.target.checked
        });
    };

    const handlePurposeChange = (e: any) => {
        onQuestionUpdate({
            ...question,
            purpose: e.target.value as 'text' | 'email' | 'tel'
        });
    };

    return (
        <Stack
            onClick={onSelect}
            sx={{
                p: 3,
                borderRadius: 1,
                border: isSelected ? '2px solid' : '1px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                    borderColor: 'primary.light',
                    backgroundColor: 'action.hover'
                },
                position: 'relative'
            }}
        >
            <Box sx={{alignSelf: 'flex-end'}}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={question.required || false}
                            onChange={handleRequiredChange}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                        />
                    }
                    label={<Typography variant="caption">{t('Required')}</Typography>}
                    labelPlacement="start"
                    onClick={(e) => e.stopPropagation()}
                    sx={{m: 0}}
                />
            </Box>

            <TextField
                fullWidth
                label={t('Question Label')}
                value={question.title}
                onChange={handleTitleChange}
                variant="standard"
                sx={{mb: 2, pr: 12}}
                onClick={(e) => e.stopPropagation()}
            />

            {isSelected ? (
                <Box sx={{mb: 2}} onClick={(e) => e.stopPropagation()}>
                    <MdEditor
                        value={question.description || ''}
                        onChange={handleDescriptionChange}
                        placeholder={t('Question Description (optional)')}
                        className={'mini-mdx'}
                    />
                </Box>
            ) : (
                question.description && (
                    <Box sx={{mb: 2}}>
                        <MdRenderer>
                            {question.description}
                        </MdRenderer>
                    </Box>
                )
            )}

            <TextField
                fullWidth
                placeholder={question.placeholder || t('Type your answer here...')}
                disabled
                variant="outlined"
                size="small"
                sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)'
                    }
                }}
            />

            {isSelected && (
                <Box sx={{mt: 2}}>
                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                        {t('Input Type')}
                    </Typography>
                    <FormControl fullWidth size="small" sx={{mb: 2}}>
                        <Select
                            value={question.purpose || 'text'}
                            onChange={handlePurposeChange}
                            onClick={(e) => e.stopPropagation()}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        bgcolor: '#fff',
                                    },
                                },
                            }}
                        >
                            <MenuItem value="text">{t('Text')}</MenuItem>
                            <MenuItem value="email">{t('Email')}</MenuItem>
                            <MenuItem value="tel">{t('Phone Number')}</MenuItem>
                        </Select>
                    </FormControl>

                    <Typography variant="caption" color="text.secondary" sx={{display: 'block', mb: 1}}>
                        {t('Placeholder Text')}
                    </Typography>
                    <TextField
                        fullWidth
                        value={question.placeholder || ''}
                        onChange={handlePlaceholderChange}
                        variant="outlined"
                        size="small"
                        placeholder={t('Enter placeholder text...')}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Box>
            )}
        </Stack>
    );
};

export default ShortTextInput;
